import { useEffect, useState } from 'react';
import { formatFranc } from '../lib/utils';
import api from '../lib/api';
import { getAvatarSource, getInitialsAvatarUrl } from '../lib/avatars';
import { TrendingUp, TrendingDown, Zap } from 'lucide-react';

// 2026-08-12 (owner request) — "Rival Portfolio Insights" on the team drill-in
// (below the net-worth plot): the roster's biggest movers (current price vs
// what the manager paid — acquired_price is public on finalized rosters) and
// the biggest recent scoring events across the roster.

const prettyEvent = (t) => (t || 'event').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function EntityAvatar({ mover, size = 28 }) {
  const { src } = getAvatarSource(
    { metadata: { thumbnail: mover.thumbnail }, name: mover.name || mover.entity_name, entity_type: mover.entity_type },
    size,
  );
  return (
    <img
      src={src}
      alt={mover.name || mover.entity_name}
      onError={(e) => { e.currentTarget.src = getInitialsAvatarUrl(mover.name || mover.entity_name); }}
      className="rounded-full object-cover bg-muted flex-shrink-0"
      style={{ width: size, height: size }}
    />
  );
}

function MoverRow({ m, positive, onEntityClick }) {
  return (
    <button
      type="button"
      onClick={() => onEntityClick?.(m.entity_id)}
      className="w-full flex items-center gap-2 py-1.5 text-left hover:bg-muted/40 rounded-lg px-1 transition-colors"
      data-testid={`mover-${m.entity_id}`}
    >
      <EntityAvatar mover={m} />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium truncate">{m.name}</p>
        <p className="text-[10px] text-muted-foreground tabular-nums">
          {formatFranc(m.acquired_price)} → {formatFranc(m.current_price)}
        </p>
      </div>
      <div className={`text-right tabular-nums ${positive ? 'text-[hsl(var(--positive-text))]' : 'text-destructive'}`}>
        <p className="text-xs font-bold">{positive ? '▲' : '▼'}{formatFranc(Math.abs(m.delta))}</p>
        <p className="text-[10px]">{positive ? '+' : ''}{m.pct.toFixed(0)}%</p>
      </div>
    </button>
  );
}

export default function RivalInsights({ teamId, onEntityClick }) {
  const [data, setData] = useState(null);
  const [range, setRange] = useState('week');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!teamId) return undefined;
    let on = true;
    setLoading(true);
    api.get(`/teams/insights/${teamId}`, { params: { window: range } })
      .then((res) => { if (on) setData(res.data); })
      .catch(() => { if (on) setData(null); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [teamId, range]);

  // Keep the panel mounted across window switches (movers are window-independent).
  if (!data || data.roster_hidden) return null;
  const gainers = data.movers?.gainers || [];
  const losers = data.movers?.losers || [];
  const events = data.top_events || [];
  if (!gainers.length && !losers.length && !events.length && !loading) return null;

  const TogglePill = ({ value, label }) => (
    <button
      type="button"
      onClick={() => setRange(value)}
      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full transition-colors ${
        range === value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/70'
      }`}
      data-testid={`insights-window-${value}`}
    >
      {label}
    </button>
  );

  return (
    <div className="mt-4 space-y-3" data-testid="rival-insights">
      <h3 className="text-sm font-semibold">Portfolio Insights</h3>

      {(gainers.length > 0 || losers.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {gainers.length > 0 && (
            <div className="bg-card border border-border/60 rounded-2xl p-3" data-testid="insights-gainers">
              <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1 mb-1">
                <TrendingUp className="h-3.5 w-3.5 text-[hsl(var(--positive-text))]" /> Biggest Gainers
              </p>
              {gainers.map((m) => <MoverRow key={m.entity_id} m={m} positive onEntityClick={onEntityClick} />)}
            </div>
          )}
          {losers.length > 0 && (
            <div className="bg-card border border-border/60 rounded-2xl p-3" data-testid="insights-losers">
              <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1 mb-1">
                <TrendingDown className="h-3.5 w-3.5 text-destructive" /> Biggest Losers
              </p>
              {losers.map((m) => <MoverRow key={m.entity_id} m={m} positive={false} onEntityClick={onEntityClick} />)}
            </div>
          )}
        </div>
      )}

      <div className="bg-card border border-border/60 rounded-2xl p-3" data-testid="insights-events">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
            <Zap className="h-3.5 w-3.5 text-primary" /> Biggest Events
          </p>
          <div className="flex items-center gap-1" data-testid="insights-window-toggle">
            <TogglePill value="week" label="This Week" />
            <TogglePill value="season" label="All Season" />
          </div>
        </div>
        {events.length > 0 ? (
          <div className="space-y-1.5">
            {events.map((ev, i) => (
              <button
                type="button"
                key={`${ev.entity_id}-${ev.created_at}-${i}`}
                onClick={() => onEntityClick?.(ev.entity_id)}
                className="w-full flex items-center gap-2 text-left hover:bg-muted/40 rounded-lg px-1 py-1 transition-colors"
                data-testid={`insight-event-${i}`}
              >
                <span className="text-base flex-shrink-0" aria-hidden>{ev.emoji}</span>
                <span className="text-xs font-medium truncate flex-1">{ev.entity_name}</span>
                <span className="text-[10px] text-muted-foreground truncate max-w-[45%]">{prettyEvent(ev.event_type)}</span>
                <span className="text-xs font-bold tabular-nums text-[hsl(var(--positive-text))] flex-shrink-0">{formatFranc(ev.points, { sign: true })}</span>
              </button>
            ))}
          </div>
        ) : (
          <p className="text-[11px] text-muted-foreground py-2 text-center" data-testid="insights-events-empty">
            {range === 'week' ? 'No big events this week yet — try All Season.' : 'No scoring events yet this season.'}
          </p>
        )}
      </div>
    </div>
  );
}
